import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import apiClient from "../../../../../lib/api_client";

const fetchCollectionList = async () => {
  const { data } = await apiClient.get("/getCollections");
  return data.data;
};

export const useCollections = () => {
  return useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollectionList,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: false,
  });
};

const fetchCollectionDesigns = async ({ queryKey }) => {
  const [, collectionId] = queryKey;
  const { data } = await apiClient.post("/getCollectionDesigns", { collectionId });
  return data.data;
};

export const useCollectionDesigns = ({ collectionId }) => {
  return useQuery({
    queryKey: ["collectionDesigns", collectionId],
    queryFn: fetchCollectionDesigns,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: false,
    enabled: !!collectionId,
  });
};

const fetchCategories = async () => {
  const { data } = await apiClient.get("/getCategorys");
  return data.data;
};

export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  });
};

const fetchSubCategories = async ({ queryKey }) => {
  const [, { categoryName }] = queryKey;
  const { data } = await apiClient.post("/getSubcategorys", { categoryName });
  return data.data;
};

export const useSubCategories = ({ categoryName = "" }) => {
  return useQuery({
    queryKey: ["subCategories", { categoryName }],
    queryFn: () =>
      fetchSubCategories({ queryKey: ["subcategories", { categoryName }] }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    enabled: !!categoryName,
  });
};

const fetchDesigns = async ({ pageParam = 1, queryKey }) => {
  const [, categoryName, subCategoryName, limit] = queryKey;
  const res = await apiClient.post("/getDesigns", {
    categoryName,
    subCategoryName,
    pageNo: pageParam,
    limit,
  });
  return res.data.data;
};

export function useDesigns({ categoryName, subCategoryName, limit = 14 }) {
  return useInfiniteQuery({
    queryKey: ["designs", categoryName, subCategoryName, limit],
    queryFn: fetchDesigns,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      return pagination?.currentPage < pagination?.totalPages
        ? pagination.currentPage + 1
        : undefined;
    },
    retry: false,
    enabled: !!categoryName && !!subCategoryName,
  });
}

const fetchSelectedSetDesigns = async ({ queryKey }) => {
  const [, { styleNumber, cartDesigns }] = queryKey;
  const { data } = await apiClient.post("/getSelectedSetDesigns", {
    styleNumber,
    cartDesigns,
  });
  return data.data;
};


export const useSelectedSetDesigns = ({
  styleNumber = "",
  cartDesigns = [],
}) => {
  return useQuery({
    queryKey: ["setDesigns", { styleNumber, cartDesigns }],
    queryFn: () =>
      fetchSelectedSetDesigns({
        queryKey: ["setDesigns", { styleNumber, cartDesigns }],
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    enabled: !!styleNumber && !!cartDesigns,
  });
};
